import { useMemo } from 'react';
import { Table } from 'antd';
import type { TableProps } from 'antd';

import { useT } from '../../i18n';

/** Trên ngưỡng này thì bật cuộn dọc và ghim đầu bảng. */
const SCROLL_ROW_THRESHOLD = 10;
const DEFAULT_BODY_HEIGHT = 700;

export interface DataTableProps<T extends object> extends TableProps<T> {
  /**
   * Mô tả bảng cho trình đọc màn hình, dựng thành `<caption class="sr-only">`.
   *
   * Bắt buộc: danh sách kiểm ở §14 của tài liệu tham khảo yêu cầu mọi bảng có
   * `<caption>`. antd không sinh caption, cũng không sinh `scope` trên `<th>`,
   * nên hai thứ đó phải tự thêm ở đây — thêm một lần cho mọi bảng.
   */
  caption: string;
}

/**
 * **Mọi** bảng trong ứng dụng đều đi qua component này (§9.4).
 *
 * Ba hành vi cần giữ:
 * - `rowKey` mặc định là `"id"`.
 * - Tự bật cuộn dọc + ghim đầu bảng khi có **hơn 10 dòng**. Từ 10 dòng trở
 *   xuống thì bảng vừa khít nội dung: không có khoảng trống cao vô ích, không
 *   có thanh cuộn thừa. Trang vẫn tự cuộn bằng khung ngoài.
 * - `<caption>` ẩn và `scope="col"` trên mọi ô đầu cột.
 *
 * Nền đầu bảng là `bg-subtle` (không phải trắng) — đó là lý do bảng trông có
 * dải; cấu hình ở ConfigProvider (§5.14).
 *
 * ❌ `@tanstack/react-table` bị cấm trong toàn dự án.
 */
export function DataTable<T extends object>({
  scroll,
  rowKey = 'id',
  dataSource,
  caption,
  components,
  ...props
}: DataTableProps<T>) {
  const { t } = useT();
  const rowCount = dataSource?.length ?? 0;
  /*
   * `x: 'max-content'` cho mọi bảng: §16.6 yêu cầu bảng **cuộn ngang** chứ
   * không dồn dòng khi hẹp. Thiếu nó thì antd để `overflow-x: visible` và cột
   * bên phải bị cắt mất, người dùng không có cách nào xem tới — lỗi thấy rõ ở
   * 1440px với bảng nhiều cột, nặng hơn nữa ở 768px.
   *
   * `y` vẫn chỉ bật khi quá ngưỡng dòng, giữ nguyên hành vi cũ.
   */
  const effectiveScroll = scroll ?? {
    x: 'max-content' as const,
    ...(rowCount > SCROLL_ROW_THRESHOLD ? { y: DEFAULT_BODY_HEIGHT } : {}),
  };

  // antd dựng phân trang thành <ul> trần: không landmark, không tên. Danh sách
  // kiểm §14 yêu cầu phân trang là vùng điều hướng có nhãn.
  //
  // <ul> của @rc-component/pagination spread mọi thuộc tính `data-*`/`aria-*`
  // và `role` truyền vào, nên gắn thẳng `role="navigation"` + `aria-label` lên
  // đó là đủ; không cần thẻ bọc, tránh thêm một landmark lồng nhau.
  const phanTrang =
    props.pagination === false
      ? (false as const)
      : {
          role: 'navigation',
          'aria-label': t('bang.phanTrang', { caption }),
          ...props.pagination,
        };

  /*
   * Renderer `table` và `header.cell` phải **giữ nguyên danh tính** giữa các lần vẽ. Viết thẳng arrow
   * function trong JSX thì mỗi lần vẽ là một kiểu component mới, React gỡ rồi dựng lại cả bảng: menu
   * `⋮` đang mở tự đóng, ô đang gõ mất focus mỗi khi màn tự hỏi lại server (gặp ở màn chứng từ và
   * kho bản OCR, hỏi lại mỗi 4 giây khi còn dòng đang chạy).
   */
  const thanhPhan = useMemo<TableProps<T>['components']>(
    () => ({
      ...components,
      // `<caption>` cho bảng. Khi bật `scroll.y`, antd tách thành hai
      // <table> (một giữ <thead> trong `.ant-table-header`, một giữ <tbody>
      // trong `.ant-table-body`) và **chỉ gọi renderer này cho bảng phần
      // thân** — bảng phần đầu do antd tự dựng, không đi qua đây. Vì vậy
      // caption một mình không bao giờ phủ được bảng chứa tiêu đề cột; tên
      // khả truy cập thật nằm ở `aria-label` của thẻ bọc bao ngoài.
      table: ({ children, ...rest }: React.HTMLAttributes<HTMLTableElement>) => (
        <table {...rest}>
          {/* sr-only: người xem bằng mắt đã có TieuDeTrang phía trên. */}
          <caption className="sr-only">{caption}</caption>
          {children}
        </table>
      ),
      header: {
        ...components?.header,
        // antd dựng <th> không có scope; trình đọc màn hình cần scope để
        // đọc đúng ô đầu cột nào thuộc ô dữ liệu nào.
        // Ô tiêu đề nhóm cột (`children` của antd) trải nhiều cột nên là `colgroup`.
        cell: ({ children, ...rest }: React.ThHTMLAttributes<HTMLTableCellElement>) => (
          <th {...rest} scope={(rest.colSpan ?? 1) > 1 ? 'colgroup' : 'col'}>
            {children}
          </th>
        ),
      },
    }),
    [caption, components],
  );

  return (
    /*
     * Thẻ bọc mang tên khả truy cập cho cả vùng bảng.
     *
     * Cần vì `<caption>` chỉ tới được bảng phần thân khi antd tách bảng
     * (xem chú thích ở `components.table`). `role="region"` + `aria-label`
     * cho trình đọc màn hình một tên ổn định, không phụ thuộc antd dựng một
     * hay hai <table>.
     */
    <div role="region" aria-label={caption}>
      <Table<T>
        rowKey={rowKey}
        dataSource={dataSource}
        scroll={effectiveScroll}
        components={thanhPhan}
        {...props}
        // Đặt sau {...props} để nhãn phân trang không bị ghi đè mất.
        pagination={phanTrang}
      />
    </div>
  );
}
